import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Square } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { resolveDescription } from '../hooks/useProjects';
import { useTranslation } from 'react-i18next';

const PropertyCard = ({ property }) => {
  const { t, i18n } = useTranslation();
  
  // Translate "From" / "from", "terrace" and "balcony" in text
  const translateText = (text) => {
    if (!text) return text;
    const fromTranslation = t('property.from');
    const terraceTranslation = t('property.terrace');
    const balconyTranslation = t('property.balcony');
    return text
      .replace(/^From /i, `${fromTranslation} `)
      .replace(/^from /i, `${fromTranslation} `)
      .replace(/\(terrace\)/i, `(${terraceTranslation})`)
      .replace(/\(balcony\)/i, `(${balconyTranslation})`);
  };

  // Get translated description (legacy projects use i18n, new ones use stored AI translations)
  const getDescription = () => resolveDescription(property, t, i18n.language);

  // Get translated location
  const getLocation = () => {
    const locationMap = {
      'Northern Cyprus': t('regions.northernCyprus.name'),
      'Bali (Indonesia)': t('regions.bali.name'),
      'Vietnam': t('regions.vietnam.name'),
      'Maldives': t('regions.maldives.name')
    };
    return locationMap[property.location] || property.location;
  };

  // Get translated property type
  const getPropertyType = () => {
    const typeMap = {
      'Villa': t('property.villa'),
      'Apartment': t('property.apartment'),
      'Mixed': t('property.mixed'),
      'Studio': t('property.studio')
    };
    return typeMap[property.type] || property.type;
  };
  
  return (
    <Link to={`/properties/${property.id}`} className="block" data-testid={`property-card-${property.id}`}>
    <Card className="group overflow-hidden border-[#CDA259]/20 hover:border-[#CDA259] transition-all duration-300 hover:shadow-xl bg-white cursor-pointer">
      <div className="relative overflow-hidden h-64">
        {property.image ? (
          <img
            src={property.image}
            alt={property.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full bg-[#102131]/5" />
        )}
        <div className="absolute top-4 right-4">
          <Badge className="bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90">
            {getPropertyType()}
          </Badge>
        </div>
        {property.featured && (
          <div className="absolute top-4 left-4">
            <Badge className="bg-[#102131] text-[#F7FDFF]">
              {t('property.featured')}
            </Badge>
          </div>
        )}
      </div>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-bold text-[#102131] group-hover:text-[#CDA259] transition-colors">
            {property.title}
          </h3>
        </div>
        <div className="flex items-center text-sm text-gray-600 mb-3">
          <MapPin size={16} className="mr-1 text-[#CDA259]" />
          {getLocation()}
        </div>
        <p className="text-2xl font-bold text-[#CDA259] mb-4">{translateText(property.price)}</p>
        
        {/* Display area information based on property type */}
        {property.apartmentArea || property.villaArea ? (
          <div className="space-y-2 mb-4">
            {property.apartmentArea && (
              <div className="flex items-center text-sm text-gray-600">
                <Square size={16} className="mr-2 text-[#CDA259]" />
                <span className="font-medium">{t('property.apartments')}: {translateText(property.apartmentArea)}</span>
              </div>
            )}
            {property.villaArea && (
              <div className="flex items-center text-sm text-gray-600">
                <Square size={16} className="mr-2 text-[#CDA259]" />
                <span className="font-medium">{t('property.villas')}: {translateText(property.villaArea)}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
            {property.bedrooms && (
              <div className="flex items-center">
                <Bed size={16} className="mr-1" />
                <span>{property.bedrooms} {t('property.beds')}</span>
              </div>
            )}
            {property.bathrooms && (
              <div className="flex items-center">
                <Bath size={16} className="mr-1" />
                <span>{property.bathrooms} {t('property.baths')}</span>
              </div>
            )}
            {property.area && (
              <div className="flex items-center">
                <Square size={16} className="mr-1" />
                <span>{translateText(property.area)}</span>
              </div>
            )}
          </div>
        )}
        
        <p className="text-sm text-gray-600 line-clamp-2">{getDescription()}</p>
      </CardContent>
    </Card>
    </Link>
  );
};

export default PropertyCard;
